import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import ReactDOM from "react-dom";
import ReactMarkdown from "react-markdown";
import "./TooltipWithHtml.scss";

/**
 *
 * @param {Object.<string, any>} props
 *
 * @param {function} props.tooltipContent - Tooltip content render function
 * @param {React.ReactChildren} props.children - HTML element that needs to have the tooltip
 * @param {string} [props.tooltipBodyClasses=""] - Classes applied to the body element wrapper
 * @param {string} [props.tooltipPlacement=] - Where the tooltip should be, relative to the content, Default: "bottom-end"
 * @param {boolean} [props.arrow=false] - Whether the tooltip should have a pointer arrow towards the content
 *
 * Note: When used as a button wrapper, if the button is disabled, the tooltip won't show, use btn--disabled class instead and handle action accordingly
 *
 * @returns
 */
const TooltipWithHtml = ({
	tooltipContent,
	children: tooltipBody,
	tooltipPlacement = "right",
	wrapperClasses = "",
	tooltiptextClasses = "white-space--nowrap",
	hideTooltip = false,
}) => {
	const triggerRef = useRef(null);
	const tooltipRef = useRef(null);
	const [hoveringTrigger, setHoveringTrigger] = useState(false);
	const [hoveringTooltip, setHoveringTooltip] = useState(false);
	const visible = !hideTooltip && (hoveringTrigger || hoveringTooltip);

	const [coords, setCoords] = useState({ top: 0, left: 0 });

	const renderTooltipContent = useCallback(() => {
		if (typeof tooltipContent === "function") {
			return tooltipContent();
		}

		const isEmptyTooltipContent =
			tooltipContent === null ||
			tooltipContent === undefined ||
			tooltipContent === "" ||
			tooltipContent === "-";
		if (isEmptyTooltipContent) {
			return null;
		}

		if (tooltipContent === null || tooltipContent === undefined) {
			return null;
		}

		if (typeof tooltipContent === "string") {
			return (
				<ReactMarkdown>{tooltipContent.replaceAll("\n", "  \n")}</ReactMarkdown>
			);
		}

		if (React.isValidElement(tooltipContent)) {
			return tooltipContent;
		}

		return (
			<ReactMarkdown>
				{String(tooltipContent).replaceAll("\n", "  \n")}
			</ReactMarkdown>
		);
	}, [tooltipContent]);

	const updatePosition = useCallback(() => {
		const triggerEl = triggerRef.current;
		const tooltipEl = tooltipRef.current;
		if (!triggerEl || !tooltipEl) return;

		const rect = triggerEl.getBoundingClientRect();
		const tipRect = tooltipEl.getBoundingClientRect();
		const gap = 12; // base gap between trigger and tooltip
		const arrowOffset = 6; // approximate arrow width to ensure no overlap

		let top = rect.top;
		let left = rect.left;

		switch (tooltipPlacement) {
			case "top":
				top = rect.top - tipRect.height - gap;
				left = rect.left + rect.width / 2 - tipRect.width / 2;
				break;
			case "top-start":
				top = rect.top - tipRect.height - gap;
				left = rect.left;
				break;
			case "right":
				top = rect.top + rect.height / 2 - tipRect.height / 2;
				left = rect.right + gap;
				break;
			case "left":
				top = rect.top + rect.height / 2 - tipRect.height / 2;
				left = rect.left - tipRect.width - (gap + arrowOffset);
				break;
			case "bottom-start":
				top = rect.bottom + gap;
				left = rect.left;
				break;
			case "bottom":
			default:
				top = rect.bottom + gap;
				left = rect.left + rect.width / 2 - tipRect.width / 2;
				break;
		}

		// Keep tooltip within viewport bounds without overlapping the trigger
		const vw = window.innerWidth;
		const vh = window.innerHeight;
		const margin = 8;

		// Vertical clamping applies to all placements
		if (top < margin) top = margin;
		if (top + tipRect.height + margin > vh) {
			top = vh - tipRect.height - margin;
		}

		// Horizontal clamping depends on placement to avoid pushing into the trigger
		if (tooltipPlacement === "left") {
			// Keep strictly to the left of the trigger with a gap; allow off-screen if needed
			const desiredLeft = rect.left - tipRect.width - gap;
			left = desiredLeft;
		} else if (tooltipPlacement === "right") {
			// Clamp within viewport but stay on the right
			if (left + tipRect.width + margin > vw)
				left = vw - tipRect.width - margin;
			if (left < margin) left = margin;
		} else {
			// top/bottom variants: clamp horizontally within viewport
			if (left + tipRect.width + margin > vw)
				left = vw - tipRect.width - margin;
			if (left < margin) left = margin;
		}

		setCoords({ top, left });
	}, [tooltipPlacement]);

	useLayoutEffect(() => {
		if (!visible) return;
		// Position after mount and on next frame (ensures measurement after content render)
		updatePosition();
		const raf = requestAnimationFrame(updatePosition);
		return () => cancelAnimationFrame(raf);
	}, [visible, tooltipContent, tooltipPlacement, updatePosition]);

	useEffect(() => {
		if (!visible) return;
		const handle = () => updatePosition();
		window.addEventListener("resize", handle);
		// Capture scroll from any scrolling ancestor
		document.addEventListener("scroll", handle, true);
		return () => {
			window.removeEventListener("resize", handle);
			document.removeEventListener("scroll", handle, true);
		};
	}, [visible, updatePosition]);

	const tooltipBodyContent = renderTooltipContent();
	const shouldRenderTooltip = !hideTooltip && tooltipBodyContent !== null;

	const portal = shouldRenderTooltip && (
		<span
			ref={tooltipRef}
			onMouseEnter={() => setHoveringTooltip(true)}
			onMouseLeave={() => setHoveringTooltip(false)}
			className={`tooltiptext tooltip--${tooltipPlacement} body-font body-font--small ${tooltiptextClasses}`}
			style={{
				position: "fixed",
				top: coords.top,
				left: coords.left,
				margin: 0,
				right: "auto",
				bottom: "auto",
				transform: "none",
				zIndex: 10000,
				visibility: visible ? "visible" : "hidden",
				opacity: visible ? 1 : 0,
				pointerEvents: visible ? "auto" : "none",
			}}
		>
			{tooltipBodyContent}
		</span>
	);

	return (
		<>
			<div
				ref={triggerRef}
				className={`tooltip ${wrapperClasses}`}
				onMouseEnter={() => setHoveringTrigger(true)}
				onMouseLeave={() => setHoveringTrigger(false)}
			>
				{tooltipBody}
			</div>
			{portal && ReactDOM.createPortal(portal, document.body)}
		</>
	);
};

export default TooltipWithHtml;
